import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, TrendingUp, Wallet, ShoppingBag, BadgeCheck, Crown, Users, AlertCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const fmtMoney = (n, ccy = "GBP") => {
  const sym = { USD: "$", GBP: "£", EUR: "€", CAD: "C$" }[ccy] || `${ccy} `;
  return `${sym}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const Tile = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div className="bg-white border border-black/10 rounded-xl p-6" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="label-overline text-muted-foreground">{label}</div>
      <Icon className={`w-4 h-4 ${accent || "text-[#C05A3C]"}`}/>
    </div>
    <div className="font-display text-4xl mt-3 tabular-nums">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const AdminRevenue = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/dashboard"); return;
    }
    api.get("/admin/stats/revenue")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Forbidden"));
  }, [user, navigate]);

  if (error) {
    return <div className="container-narrow py-20 text-center text-muted-foreground">{error}</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]"/>
      </div>
    );
  }

  const ccy = data.headline.currency;

  return (
    <div className="bg-[#FAF8F5] min-h-screen py-10">
      <div className="container-narrow">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-overline text-[#C05A3C] mb-2">Admin · Revenue dashboard</div>
            <h1 className="font-display text-4xl tracking-tight">AfriMarket pulse</h1>
            <p className="text-muted-foreground text-sm mt-1">Live financials + growth — share this screen in pitch meetings.</p>
          </div>
          <div className="text-xs text-muted-foreground">All amounts {ccy}</div>
        </div>

        {/* Headline revenue */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Tile icon={Wallet} testid="rev-total" label="Total revenue (platform)" value={fmtMoney(data.headline.total_revenue, ccy)} sub="Fees + badges + Pro"/>
          <Tile icon={ShoppingBag} testid="rev-gmv" label="Listings GMV" value={fmtMoney(data.headline.listings_gmv, ccy)} sub={`${fmtMoney(data.headline.listings_fees, ccy)} platform fees`}/>
          <Tile icon={BadgeCheck} testid="rev-badge" label="Verified badge revenue" value={fmtMoney(data.headline.badge_revenue, ccy)} sub="£19.99 one-off per seller"/>
          <Tile icon={Crown} testid="rev-pro" label="Pro subscription revenue" value={fmtMoney(data.headline.pro_revenue, ccy)} sub="£4.99–9.99 / month"/>
        </div>

        {/* Operational counts */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Tile icon={Users} testid="cnt-users" label="Total users" value={data.counts.users} sub={`${data.counts.sellers} verified sellers`}/>
          <Tile icon={AlertCircle} testid="cnt-pending" accent="text-amber-600" label="Pending review queue" value={data.counts.pending_kyc + data.counts.pending_listings} sub={`${data.counts.pending_kyc} KYC · ${data.counts.pending_listings} listings`}/>
          <Tile icon={Crown} testid="cnt-founders" label="Pro founders" value={`${data.counts.pro_founders} / 100`} sub={`${data.counts.founders_remaining} spots remaining`}/>
        </div>

        {/* 30-day growth chart */}
        <div className="bg-white border border-black/10 rounded-2xl p-6 mb-8" data-testid="growth-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-overline text-muted-foreground">Last 30 days</div>
              <h2 className="font-display text-2xl">Growth pulse</h2>
            </div>
            <TrendingUp className="w-5 h-5 text-[#C05A3C]"/>
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <LineChart data={data.days} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#00000010"/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#88837A"/>
                <YAxis tick={{ fontSize: 11 }} stroke="#88837A" allowDecimals={false}/>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #00000020", fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Line type="monotone" dataKey="signups" stroke="#C05A3C" strokeWidth={2.5} dot={false} name="Signups"/>
                <Line type="monotone" dataKey="listings" stroke="#2A3D36" strokeWidth={2.5} dot={false} name="New listings"/>
                <Line type="monotone" dataKey="transactions" stroke="#D9923B" strokeWidth={2.5} dot={false} name="Paid transactions"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue breakdown table */}
        <div className="bg-white border border-black/10 rounded-2xl overflow-hidden" data-testid="breakdown-table">
          <div className="px-6 py-4 border-b border-black/10">
            <h2 className="font-display text-2xl">Revenue by payment type</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F5]">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3 text-right">Transactions</th>
                <th className="px-6 py-3 text-right">GMV</th>
                <th className="px-6 py-3 text-right">Platform revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.by_payment_type.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">No paid transactions yet — drive your first sale today.</td></tr>
              ) : data.by_payment_type.map((row) => (
                <tr key={row.type} className="border-t border-black/10">
                  <td className="px-6 py-4 capitalize">{row.type.replace("_", " ")}</td>
                  <td className="px-6 py-4 text-right tabular-nums">{row.count}</td>
                  <td className="px-6 py-4 text-right tabular-nums">{fmtMoney(row.gmv, ccy)}</td>
                  <td className="px-6 py-4 text-right tabular-nums font-semibold">{fmtMoney(row.fees || row.gmv, ccy)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-center text-xs text-muted-foreground mt-8">
          Internal data — do not share publicly. Public counters live at <a href="/stats" className="text-[#C05A3C]">/stats</a>.
        </div>
      </div>
    </div>
  );
};

export default AdminRevenue;
